import { useCallback, useEffect, useState } from 'react'
import { api, type SlackConnection } from '../api'
import { Icon } from './Icon'

/**
 * "Search this documentation from Slack" — the connection card.
 *
 * It is the GitHub card's sibling and follows its shape: a deployment with no
 * Slack app says so and offers nothing, a project with no installation offers
 * one button that leaves the product, and every state has a sentence.
 *
 * Two things make it different, and both are decisions rather than details.
 *
 * **It says what will not work.** Link previews — Slack unfurling a
 * documentation link into its title and first sentence — only ever happen on
 * the domains the Slack app itself declares, up front, in a short list, on
 * hosts we control. A customer publishing on `docs.acme.com` cannot have one,
 * however the app is installed and whatever they grant. A card that stayed
 * quiet about that would be selling a feature that cannot be delivered, and the
 * person would find out by pasting a link into a channel and watching nothing
 * happen. So it is written on the card, beside the two things that DO work
 * everywhere.
 *
 * **It says which half of removing it is ours.** Disconnecting here forgets the
 * token; the app stays in their Slack until they remove it there. Doing that
 * for them — revoking our own access from inside their chat workspace — is not
 * a thing to do quietly, which is the same line the repository card draws.
 */
export function ConnectedSlack({ mayChange }: { mayChange: boolean }) {
  const [state, setState] = useState<SlackConnection | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [note, setNote] = useState<string | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const [channels, setChannels] = useState<Record<string, string>>({})

  const load = useCallback(async () => {
    try {
      const found = await api.slackConnection()
      setState(found)
      setChannels(
        Object.fromEntries(found.installations.map((one) => [one.teamId, one.alertChannel ?? ''])),
      )
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    }
  }, [])
  useEffect(() => void load(), [load])

  /**
   * What the round trip to Slack said, read once from the address it came back
   * on.
   *
   * The callback cannot render a screen — it is a redirect from slack.com — so
   * the reason travels on the query string and is turned into a sentence here.
   * Removed from the address afterwards, so a reload does not re-announce
   * something that happened ten minutes ago.
   */
  useEffect(() => {
    const said = new URLSearchParams(window.location.search).get('slack')
    if (!said) return
    const words: Record<string, string> = {
      ok: 'Slack is connected. Type /docs in any channel to search this documentation.',
      cancelled: 'Nothing was connected — the app was not added.',
      expired: 'That took too long, or the link had been changed. Press Add to Slack again.',
      'not-allowed': 'Only an owner or an admin of this workspace can connect Slack to it.',
      taken:
        'That Slack workspace is already connected to another Staleless workspace. Remove it there first.',
      refused: 'Slack would not finish adding the app. Try again.',
      'key-missing':
        'This installation of Staleless cannot store a Slack token. Tell whoever runs it.',
      key_missing:
        'This installation of Staleless cannot store a Slack token. Tell whoever runs it.',
      'no-code': 'Slack sent us back without anything to exchange. Press Add to Slack again.',
      unavailable: 'This installation of Staleless has no Slack app.',
    }
    const sentence = words[said] ?? 'Slack did not finish connecting.'
    if (said === 'ok') setNote(sentence)
    else setError(sentence)
    const url = new URL(window.location.href)
    url.searchParams.delete('slack')
    window.history.replaceState({}, '', url.toString())
  }, [])

  /** One busy key rather than a boolean, the idiom every Settings panel uses. */
  const act = useCallback(
    async (key: string, work: () => Promise<unknown>, done?: string) => {
      setBusy(key)
      setError(null)
      setNote(null)
      try {
        await work()
        await load()
        if (done) setNote(done)
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e))
      } finally {
        setBusy(null)
      }
    },
    [load],
  )

  if (!state) {
    return (
      <section className="settings__card" data-slack-card>
        <h2>Slack</h2>
        <span className="skeleton" />
      </section>
    )
  }

  /**
   * A deployment with no Slack app says so, and offers nothing. A button that
   * is present and broken is worse than one that is not there.
   */
  if (!state.configured) {
    return (
      <section className="settings__card" data-slack-card>
        <h2>Slack</h2>
        <p className="home__quiet">
          This installation of Staleless has no Slack app, so this documentation cannot be searched
          from a channel.
        </p>
      </section>
    )
  }

  return (
    <section className="settings__card" data-slack-card>
      <h2>Slack</h2>
      <p className="home__quiet">
        Add the app to your Slack workspace and anybody there can type <code>/docs refunds</code> to
        search this documentation, or <code>/docs how do refunds work?</code> to ask it a question.
        Pages that fall due for review can be announced in a channel.
      </p>

      {error && (
        <p className="screen__error">
          <Icon name="alert" size={13} /> {error}
        </p>
      )}
      {note && (
        <p className="screen__note">
          <Icon name="check" size={13} /> {note}
        </p>
      )}

      {state.installations.length === 0 && (
        <>
          {mayChange && state.installUrl && (
            <div className="settings__row">
              <a className="btn btn--primary" href={state.installUrl} data-slack-install>
                <Icon name="link" size={13} /> Add to Slack
              </a>
            </div>
          )}
          {!mayChange && <p className="home__quiet">An owner or an admin can add it.</p>}
        </>
      )}

      {state.installations.map((installed) => (
        <div key={installed.teamId} className="settings__block">
          <p className="screen__note">
            <Icon name="check" size={13} /> Connected to{' '}
            <strong>{installed.teamName ?? installed.teamId}</strong>, added by{' '}
            {installed.installedName}.
          </p>

          <div className="field">
            <label className="label" htmlFor={`slack-channel-${installed.teamId}`}>
              Channel for stale-page alerts
            </label>
            <div className="settings__row">
              <input
                id={`slack-channel-${installed.teamId}`}
                className="input"
                value={channels[installed.teamId] ?? ''}
                placeholder="C01ABCD2EFG"
                disabled={!mayChange}
                onChange={(e) =>
                  setChannels((all) => ({ ...all, [installed.teamId]: e.target.value }))
                }
              />
              {mayChange && (
                <button
                  className="btn"
                  disabled={busy !== null}
                  data-slack-alerts
                  onClick={() =>
                    void act(
                      `alerts:${installed.teamId}`,
                      () =>
                        api.setSlackAlertChannel(
                          installed.teamId,
                          (channels[installed.teamId] ?? '').trim() || null,
                        ),
                      'Saved.',
                    )
                  }
                >
                  {busy === `alerts:${installed.teamId}` ? <Icon name="spinner" size={13} /> : null}{' '}
                  Save
                </button>
              )}
            </div>
            <p className="field__note">
              The channel’s ID, not its name — open it in Slack, choose “View channel details”, and
              copy the ID at the bottom. Leave it empty and nothing is posted.
            </p>
            {/*
              Said here rather than left to arrive as a Slack error code in a
              log nobody reads. Which permissions an installation holds is a
              fact about the day it was added.
            */}
            {installed.alertProblem && (
              <p className="screen__warn" data-slack-scope>
                <Icon name="info" size={13} /> {installed.alertProblem}
              </p>
            )}
          </div>

          {mayChange && (
            <Disconnect
              busy={busy}
              workspace={installed.teamName ?? installed.teamId}
              onDisconnect={() =>
                void act(
                  'disconnect',
                  () => api.disconnectSlack(installed.teamId),
                  'Disconnected. Remove the app in Slack as well, if you want it gone from there.',
                )
              }
            />
          )}
        </div>
      ))}

      {/*
        The honest limit, on the card where the claim would otherwise be made.
        `/docs` and the alerts work in any workspace; link previews cannot,
        because Slack takes unfurl domains from the app's own settings and they
        have to be domains we control.
      */}
      <p className="field__note" data-slack-unfurl-note>
        Link previews are the one part that does not travel: Slack expands a shared documentation
        link into its title and first line only for{' '}
        {state.unfurlDomains.length > 0 ? (
          state.unfurlDomains.map((domain, at) => (
            <span key={domain}>
              {at > 0 ? ', ' : ''}
              <code>{domain}</code>
            </span>
          ))
        ) : (
          <>the domains this Slack app declares</>
        )}
        , because Slack takes that list from the app itself rather than from your installation. A
        site published on your own domain gets <code>/docs</code> and the alerts, and no previews.
      </p>
    </section>
  )
}

/**
 * Disconnecting, confirmed in place.
 *
 * Inline rather than `window.confirm`, which this codebase forbids and has a
 * test to prove it. Worth confirming because it stops a channel that people
 * may be using every day — and the sentence says the part that is NOT done for
 * them, so nobody walks away believing the app is gone from their Slack.
 */
function Disconnect({
  busy,
  workspace,
  onDisconnect,
}: {
  busy: string | null
  workspace: string
  onDisconnect: () => void
}) {
  const [asking, setAsking] = useState(false)
  if (!asking) {
    return (
      <div className="settings__row">
        <button
          className="btn btn--ghost btn--sm"
          disabled={busy !== null}
          data-slack-disconnect
          onClick={() => setAsking(true)}
        >
          Disconnect
        </button>
      </div>
    )
  }
  return (
    <div className="pubs__confirm">
      <p className="home__quiet">
        <code>/docs</code> will stop working in <strong>{workspace}</strong> and no more alerts will
        be posted. We forget the token; the app stays listed in your Slack until you remove it
        there.
      </p>
      <div className="pubs__actions">
        <button className="btn btn--danger btn--sm" disabled={busy !== null} onClick={onDisconnect}>
          {busy === 'disconnect' ? <Icon name="spinner" size={13} /> : null} Disconnect
        </button>
        <button className="btn btn--ghost btn--sm" onClick={() => setAsking(false)}>
          Keep it connected
        </button>
      </div>
    </div>
  )
}
